import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    ViewChild,
    DOCUMENT,
    ChangeDetectorRef,
} from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfigurationService } from '../core/services/configuration.service';
import { FormService } from 'src/sb-modules/Frontend-form-field-module/common/services/form.service';
import { FormCommonDataService } from 'src/sb-modules/Frontend-form-field-module/common/services/form-common-data.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalizationLanguage } from 'src/sb-tools/typescript/enums/localization-language.enum';
import { TicketService } from '../core/services/ticket.service';
import { CookieService } from 'src/sb-tools/typescript/authentication/cookie.service';
import { AddTicketModel } from 'src/sb-modules/Frontend-form-field-module/common/models/add-ticket-model.model';
import { forkJoin, finalize, takeUntil, Subject } from 'rxjs';
import { AttachmentService } from 'src/sb-tools/typescript/services/attachment.service';
import { ReCaptchaComponent } from 'src/sb-modules/Frontend-form-field-module/form-field/recaptcha/recaptcha.component';
import { environment } from '@environment';
import { GoogleAnalyticsService } from '../core/services/google-analytics.service';
import { isPlatformBrowser, isPlatformServer, NgClass } from '@angular/common';
import { FormFieldsValidationService } from 'src/sb-modules/Frontend-form-field-module/common/services/form-fields-validation.service';
import { TicketStatus } from '@sb-tools/typescript/enums/ticket-status.enum';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { FormStep } from '../core/enums/form-step.enum';
import { ClientForm } from '@form-field-module/common/models/client-form.model';
import { FormCategoryPreselectionService } from '@form-field-module/common/services/form-category-preselection.service';
import { MessageStorageService } from '@form-field-module/common/services/message-storage.service';
import { CustomerConfigurationService } from '../core/services/customer-configuration.service';
import { CustomerSettings } from '@sb-tools/typescript/models/customer-settings.model';
import { NotificationsService } from 'angular2-notifications';
import { FormType } from '@sb-tools/typescript/enums/form-type.enum';
import { AddSurveyBody } from '@form-field-module/common/models/add-survey-body.model';
import { SurveyService } from '../core/services/survey.service';
import { MagicWidgetService } from '@sb-tools/typescript/services/magic-widget.service';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TagService } from '@sb-tools/typescript/services/tag.service';
import { CategoryService } from '@sb-tools/typescript/services/category.service';
import { AssignCategoryAndTagsService } from '@form-field-module/common/services/assign-category-and-tags.service';
import { CheckFormInitDirective } from '@form-field-module/common/directives/check-form-init.directive';
import { FormFieldComponent } from '@form-field-module/form-field/form-field.component';
import { CheckFieldRulesDirective } from '@form-field-module/common/directives/check-field-rules.directive';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { InlineSVGComponent } from '../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { SafeHtmlPipe } from '@sb-tools/typescript/pipes/safe-html.pipe';
import { FooterComponent } from '../../sb-modules/Frontend-Footer-Module/footer.component';
import { CookieLawBannerComponent } from '../../sb-modules/Frontend-Cookie-Law-Banner-Module/cookie-law-banner.component';
import { ReplaceEmojisPipe } from '@sb-tools/typescript/pipes/replace-emoji/replace-emojis.pipe';
import { ReplyTemplateManagerService } from '@sb-tools/typescript/services/reply-template-manager.service';
import { NewLinePipe } from '@sb-tools/typescript/pipes/new-line.pipe';

const defaultFaviconHref = 'sb-tools/theme/images/sb-logo/zisson-socialboards-24px.svg';

@Component({
    selector: 'app-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss'],
    providers: [{ provide: AssignCategoryAndTagsService }, ReplaceEmojisPipe, ReplyTemplateManagerService, NewLinePipe],
    imports: [
        CheckFormInitDirective,
        FormsModule,
        NgClass,
        FormFieldComponent,
        CheckFieldRulesDirective,
        TranslateModule,
        ReCaptchaComponent,
        BsDropdownModule,
        InlineSVGComponent,
        SafeHtmlPipe,
        FooterComponent,
        CookieLawBannerComponent,
        ModalModule,
    ],
})
export class FormComponent implements OnInit, OnDestroy {
    @ViewChild(ReCaptchaComponent) reCaptcha: ReCaptchaComponent;
    @ViewChild('ticketForm') ticketForm: NgForm;

    favicon: HTMLLinkElement = this.document.querySelector('#favicon');
    form: ClientForm;
    formStepEnum = FormStep;
    formTypeEnum = FormType;
    initialForm: ClientForm;
    isAssignedToCreator: boolean;
    isFormReady: boolean;
    isLoading: boolean;
    isCreateAnotherTicket: boolean;
    isReCaptchaEnabled: boolean;
    reCaptchaLanguage: string;
    isSaving: boolean;
    isUploadingFiles: boolean;
    step: FormStep = FormStep.fillingForm;
    ticketStatus: TicketStatus = null;
    isTicketSentAsLink = false;

    private widgetLanguage: 'no' | 'se' | 'da' | 'en' | 'fi';
    private unsubscriber = new Subject<void>();

    constructor(
        public formService: FormService,
        public formCommonDataService: FormCommonDataService,
        public commonDataService: CommonDataService,
        private formFieldsValidationService: FormFieldsValidationService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private configurationService: ConfigurationService,
        private attachmentService: AttachmentService,
        private activatedRoute: ActivatedRoute,
        private ticketService: TicketService,
        private cookieService: CookieService,
        private translateService: TranslateService,
        private router: Router,
        private changeDetectorRef: ChangeDetectorRef,
        private customerConfigurationService: CustomerConfigurationService,
        private formCategoryPreselectionService: FormCategoryPreselectionService,
        private messageStorageService: MessageStorageService,
        private notificationsService: NotificationsService,
        private surveyService: SurveyService,
        private magicWidgetService: MagicWidgetService,
        private tagService: TagService,
        private categoryService: CategoryService,
        private assignCategoryAndTagsService: AssignCategoryAndTagsService,
        @Inject(PLATFORM_ID) private platformId: string,
        @Inject(DOCUMENT) private document: Document,
    ) {}

    ngOnInit(): void {
        if (this.formCommonDataService.isSurveySent || isPlatformServer(this.platformId)) {
            return;
        }
        this.commonDataService.communityName = this.activatedRoute.snapshot.params.community;
        this.formCommonDataService.community = this.activatedRoute.snapshot.params.community;
        this.formCommonDataService.faqName = this.activatedRoute.snapshot.queryParams.faqName;
        this.formCommonDataService.isFbApp = this.activatedRoute.snapshot.queryParams.isFbApp;
        this.formCommonDataService.isFromFaq = this.activatedRoute.snapshot.queryParams.ref === 'faq';
        this.formCommonDataService.backUrl = this.activatedRoute.snapshot.queryParams.backUrl;

        this.configurationService
            .get()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                this.formCommonDataService.customerSettings = data;
                this.processForm(this.formCommonDataService.form);
                this.processFaviconIcon();
            });

        this.attachmentService.isUploadingFilesFinished.pipe(takeUntil(this.unsubscriber)).subscribe((isFinished) => {
            this.isUploadingFiles = !isFinished;
        });

        if (this.formCommonDataService.isInternal) {
            this.customerConfigurationService
                .get()
                .pipe(takeUntil(this.unsubscriber))
                .subscribe((customerSettings: CustomerSettings) => {
                    this.commonDataService.customerSettings = customerSettings;
                });

            this.isLoading = true;

            forkJoin({
                categories: this.categoryService.get(true),
                globalTags: this.tagService.getAll(),
            })
                .pipe(
                    takeUntil(this.unsubscriber),
                    finalize(() => {
                        this.isLoading = false;
                    }),
                )
                .subscribe(({ categories, globalTags }) => {
                    this.commonDataService.categories.all = categories;

                    this.tagService.globalTags = [...globalTags].sort((a: { text: string }, b: { text: string }) =>
                        a.text.toLowerCase().localeCompare(b.text.toLowerCase()),
                    );
                });
        }
    }

    backToForm(): void {
        this.step = FormStep.fillingForm;
        $('#formContainer').scrollTop(0);
    }

    resetForm(): void {
        this.formService.isSubmitted = false;
        this.ticketStatus = null;
        this.isTicketSentAsLink = false;

        if (this.isReCaptchaEnabled) {
            this.reCaptcha.reset();
        }
        this.form = JSON.parse(JSON.stringify(this.initialForm));
    }

    async send(): Promise<void> {
        this.formService.isSubmitted = true;
        this.isSaving = true;
        const isFormValid =
            this.ticketForm.valid && this.formFieldsValidationService.isRequiredValuesFilled(this.form.fields);

        if (
            this.formCommonDataService.isInternal &&
            this.ticketStatus === TicketStatus.completed &&
            this.formFieldsValidationService.hasMissingRequiredForTeamFields(this.form.fields)
        ) {
            this.notificationsService.error(
                this.translateService.instant('Error!'),
                this.translateService.instant('Required for Agents fields must be filled to close the ticket'),
            );
            this.isSaving = false;
            return;
        }

        if (isFormValid) {
            if (this.isReCaptchaEnabled) {
                this.validateRecaptcha();
            } else {
                if (this.form.type === FormType.survey) {
                    this.processSendSurvey();
                } else {
                    this.processCreateTicket();
                }
            }
        } else if (this.isReCaptchaEnabled && this.reCaptcha.hasResponse) {
            this.reCaptcha.reset();
            this.scrollToInvalidField();
        } else {
            this.scrollToInvalidField();
        }
    }

    prepareTicketCreation(isClosedTicket: boolean, isTicketSentAsLink = false): void {
        this.ticketStatus = isClosedTicket ? TicketStatus.completed : null;
        this.isTicketSentAsLink = isTicketSentAsLink;
    }

    private processCreateTicket(): void {
        const ticketModel = new AddTicketModel(
            this.form,
            this.ticketStatus,
            this.formFieldsValidationService.checkAndSetValidValues.bind(this.formFieldsValidationService),
            this.isTicketSentAsLink,
        );

        ticketModel.isAssignedToCreator = this.isAssignedToCreator;

        if (ticketModel.message) {
            ticketModel.message = ticketModel.message.replaceAll('<div></div>', '<div><br></div>');
        }

        this.assignCategoryAndTagsService
            .assignCategoryAndTags(ticketModel, true)
            .pipe(
                takeUntil(this.unsubscriber),
                finalize(() => {
                    this.isSaving = false;
                }),
            )
            .subscribe((data) => {
                if (this.formCommonDataService.isInternal) {
                    this.ticketService
                        .create(data)
                        .pipe(
                            takeUntil(this.unsubscriber),
                            finalize(() => {
                                this.isSaving = false;
                            }),
                        )
                        .subscribe((ticketId: number) => {
                            this.formCategoryPreselectionService.saveSelectedCategoryIfNecessary(
                                this.form.fields,
                                data,
                            );

                            this.messageStorageService.clearDraft(false);
                            if (this.isCreateAnotherTicket) {
                                this.isFormReady = false;
                                this.resetForm();
                                this.changeDetectorRef.detectChanges();
                                this.isFormReady = true;
                                this.notificationsService.success(
                                    this.translateService.instant('Success!'),
                                    this.translateService.instant('New ticket was successfully created.'),
                                );
                            } else {
                                this.document.defaultView.location.href =
                                    environment.inboxUrl +
                                    '/' +
                                    this.formCommonDataService.community +
                                    '/ticket?ticketId=' +
                                    ticketId;
                            }
                        });
                } else {
                    this.ticketService
                        .send(data)
                        .pipe(
                            takeUntil(this.unsubscriber),
                            finalize(() => {
                                this.isSaving = false;
                            }),
                        )
                        .subscribe(() => {
                            this.formCategoryPreselectionService.saveSelectedCategoryIfNecessary(
                                this.form.fields,
                                data,
                            );

                            this.resetForm();
                            this.step = FormStep.formSentMessage;
                            $('#formContainer').scrollTop(0);
                        });
                }
            });
    }

    private processFaviconIcon(): void {
        if (this.formCommonDataService.form.faviconUrl) {
            this.favicon.href = this.formCommonDataService.form.faviconUrl;
        } else {
            this.favicon.href = defaultFaviconHref;
        }
    }

    private processForm(data: ClientForm): void {
        this.form = data;

        if (data.options.styles) {
            const head = this.document.getElementsByTagName('head')[0];
            const style = this.document.createElement('style') as any;
            head.appendChild(style);
            style.type = 'text/css';
            if (style.styleSheet) {
                // This is required for IE8 and below.
                style.styleSheet.cssText = data.options.styles;
            } else {
                style.appendChild(this.document.createTextNode(data.options.styles));
            }
        }
        this.formService.isHiddenCategoriesAllowed = this.form.isHiddenCategoriesAllowed;
        this.formService.activeSentimentValue = '';

        switch (data.language) {
            case LocalizationLanguage.norwegian:
                this.reCaptchaLanguage = 'no';
                this.widgetLanguage = 'no';
                break;
            case LocalizationLanguage.swedish:
                this.reCaptchaLanguage = 'sv';
                this.widgetLanguage = 'se';
                break;
            case LocalizationLanguage.danish:
                this.reCaptchaLanguage = 'da';
                this.widgetLanguage = 'da';
                break;
            case LocalizationLanguage.finnish:
                this.reCaptchaLanguage = 'fi';
                this.widgetLanguage = 'fi';
                break;
            default:
                this.reCaptchaLanguage = this.widgetLanguage = 'en';
        }

        if (!this.form.faqLink) {
            this.form.faqLink = this.form.defaultFaqLink;

            if (this.formCommonDataService.faqName) {
                if (this.form.defaultFaqLink[this.form.defaultFaqLink.length - 1] === '/') {
                    this.form.faqLink += this.formCommonDataService.faqName;
                } else {
                    this.form.faqLink += '/' + this.formCommonDataService.faqName;
                }
            }
        }

        if (isPlatformBrowser(this.platformId)) {
            if (this.formCommonDataService.customerSettings.isGAEnabled) {
                if (this.form.googleTagManager) {
                    this.googleAnalyticsService.initGoogleTagManager(this.form.googleTagManager);
                }

                this.googleAnalyticsService.initGoogleAnalytics(this.form.googleAnalytics);
            } else {
                this.cookieService.deleteGoogleAnalyticsCookies(this.router.url.split('?')[0]);
                this.document.defaultView.localStorage.removeItem('_grecaptcha');
            }
        }

        // @TODO do we use fbApp anywhere?
        // if ($rootScope.isFbApp) {
        //     if ($rootScope.form.faqLink.indexOf('?') > -1) {
        //         $rootScope.form.faqLink += '&fbApp=true';
        //     } else {
        //         if ($rootScope.form.faqLink[$rootScope.form.faqLink.length - 1] != '/') {
        //             $rootScope.form.faqLink += '/?fbApp=true';
        //         } else {
        //             $rootScope.form.faqLink += '?fbApp=true';
        //         }
        //     }

        //     if ($rootScope.form.faqLink.indexOf('\/?') == -1) {
        //         $rootScope.form.faqLink.replace('?', '\/?');
        //     }
        // }

        if (this.form.options.isReadParamsFromUrl === 'true') {
            this.setCustomFieldsFromUrl();
        }

        this.initialForm = JSON.parse(JSON.stringify(this.form));

        if (!this.formCommonDataService.isFbApp && this.form.isChatEnabled) {
            this.magicWidgetService.init(this.formCommonDataService.community, this.widgetLanguage);
        }

        this.isReCaptchaEnabled =
            this.form.options.isReCaptchaEnabled === 'true' && this.formCommonDataService.customerSettings.isGAEnabled;

        this.isFormReady = true;
    }
    private processSendSurvey(): void {
        const survey: AddSurveyBody = {
            formId: this.form.id,
            fields: AddTicketModel.getFields(this.form.fields),
        };

        this.surveyService
            .send(survey, this.formCommonDataService.surveyToken)
            .pipe(
                takeUntil(this.unsubscriber),
                finalize(() => {
                    this.isSaving = false;
                }),
            )
            .subscribe(() => {
                this.step = FormStep.formSentMessage;
                $('#formContainer').scrollTop(0);
            });
    }

    private setCustomFieldsFromUrl(): void {
        let searchValues = this.activatedRoute.snapshot.queryParams;

        if (this.form.options.isStoreValuesInCookies === 'true') {
            let cookiesName;

            if (this.form.isDefault) {
                cookiesName = this.activatedRoute.snapshot.params.community + 'SearchArguments';
            } else {
                cookiesName =
                    this.activatedRoute.snapshot.params.community +
                    this.activatedRoute.snapshot.params.formName +
                    'SearchArguments';
            }

            if (
                Object.keys(searchValues).length > 0 &&
                Object.keys(searchValues).some((key) => {
                    return (
                        key.toLowerCase() !== 'fbapp' && key.toLowerCase() !== 'ref' && key.toLowerCase() !== 'faqname'
                    );
                })
            ) {
                this.cookieService.setCookie(cookiesName, JSON.stringify(searchValues));
            } else {
                const searchValuesJSON = this.cookieService.getCookie(cookiesName);

                if (searchValuesJSON) {
                    try {
                        searchValues = JSON.parse(searchValuesJSON);

                        const categoryIdValue = searchValues.find((val, key) => {
                            return key.toLowerCase() === 'categoryid';
                        });

                        if (categoryIdValue) {
                            // isArray check need for case when several categoryId were set in url
                            const categoryId = Array.isArray(categoryIdValue)
                                ? categoryIdValue[categoryIdValue.length - 1]
                                : categoryIdValue;

                            this.formCategoryPreselectionService.categoryIdFromUrlStoredInCookies = +categoryId;
                        }
                    } catch (ex) {}
                }
            }
        }

        for (const key in searchValues) {
            if (key.toLowerCase() !== 'categoryid') {
                const formField = this.form.fields.find((field) => {
                    return field.name.toLowerCase() === key.toLowerCase();
                });

                if (formField && searchValues[key]) {
                    if (formField.isHidden) {
                        this.formFieldsValidationService.checkAndSetValidValues(formField, searchValues[key]);
                    } else {
                        formField.value = searchValues[key];
                    }
                }
            }
        }
    }

    private scrollToInvalidField(): void {
        this.isSaving = false;

        setTimeout(() => {
            this.formFieldsValidationService.scrollToInvalidField('#newTicketForm', '#formContainer');
        }, 100);
    }

    private async validateRecaptcha(): Promise<void> {
        if (!this.reCaptcha.hasResponse) {
            this.reCaptcha.markAsInvalid();
            this.isSaving = false;
        } else {
            const reCaptchaIsValid = (await this.reCaptcha.validationPromise).success;

            if (reCaptchaIsValid) {
                this.processCreateTicket();
            } else {
                this.isSaving = false;
                this.reCaptcha.reset();
            }
        }
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
